import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DxNumberBoxModule, DxButtonModule, DxPopupModule } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { environment } from 'src/environments/environment';
import { CrudService } from '../../../services/crud.service';

/**
 * Customer Portal — Outlier Threshold settings (Story 3.6, design-spec §3g / §5.5.1).
 *
 * The only Settings sub-page in the Customer Portal (reached via the gear link in the dashboard
 * header). It is the WRITE path for the `config` key `outlier_threshold_customer` that the Epic 1
 * `DiscountTierService` already READS for `is_outlier` resolution — this page never does any outlier
 * maths and never holds the threshold for flagging; it only persists the value the server reads back.
 *
 * pp-only (BR-07) — a single percentage-point number input, no `$` arm, no list/case price. Save is
 * enabled on any NON-NEGATIVE numeric value (`0` is valid and distinct from "unconfigured"; clearing
 * is a separate, confirmed action). On a successful save/clear the user is returned to the dashboard,
 * whose Pending Review + Update Requests tabs re-fetch and re-evaluate their flags (AC4) — no full
 * page reload.
 *
 * Theme/`--dx-*` tokens only — no prototype hex (the design spec's bespoke palette is directional
 * reference). Australian English; AA contrast + visible focus + ARIA labels.
 */
@Component({
  selector: 'app-customer-settings',
  templateUrl: './customer-settings.component.html',
  styleUrls: ['../../shared/pricing-layout.css', './customer-settings.component.css'],
  imports: [CommonModule, RouterModule, DxNumberBoxModule, DxButtonModule, DxPopupModule],
  standalone: true,
})
export class CustomerSettingsComponent implements OnInit {
  /** The working input value (pp), or null when the field is empty. */
  value: number | null = null;

  /** The persisted threshold last read/written (drives the "Current threshold" line + Clear link), or null. */
  currentThreshold: number | null = null;

  loading = true;
  loadError = false;
  saving = false;

  /** Clear-threshold confirmation dialog (design §3g — unlike the supplier side, Clear is confirmed). */
  clearConfirmVisible = false;

  private apiUrl: string = environment.baseApiUrl;
  private readonly endpoint = `${this.apiUrl}customer/config/outlier-threshold`;

  constructor(private crudService: CrudService, private router: Router) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  /** Fetch the current threshold and pre-populate the form. */
  private async load(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    try {
      const response: any = await firstValueFrom(this.crudService.newGetData(this.endpoint));
      const threshold = response?.data?.threshold ?? null;
      this.currentThreshold = typeof threshold === 'number' && Number.isFinite(threshold) ? threshold : null;
      this.value = this.currentThreshold;
    } catch (error) {
      console.error('Failed to load the outlier threshold', error);
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  onValueChanged(event: { value?: number | null }): void {
    this.value = event?.value ?? null;
  }

  /** True once a threshold is persisted (the "Current threshold" line + Clear link show only then). */
  get isConfigured(): boolean {
    return this.currentThreshold !== null;
  }

  /**
   * Save is enabled on any non-negative finite number (AC1; `0` is allowed — Open Q#3 resolved to
   * "non-negative" over the design-spec's "positive" wording, matching the backend `min:0` validator).
   * Clearing is the separate confirmed Clear action, not an empty Save.
   */
  get canSave(): boolean {
    return (
      !this.saving &&
      this.value !== null &&
      this.value !== undefined &&
      typeof this.value === 'number' &&
      Number.isFinite(this.value) &&
      this.value >= 0
    );
  }

  /** Persist the entered threshold, then return to the dashboard so the flagged tabs re-evaluate (AC4). */
  async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }
    this.saving = true;
    try {
      await firstValueFrom(
        this.crudService.updateDataWithoutId(this.endpoint, { threshold: this.value }),
      );
      this.currentThreshold = this.value;
      notify('Outlier threshold updated', 'success', 2500);
      // Returning to the dashboard re-instantiates it → Pending Review + Update Requests re-fetch and
      // re-evaluate is_outlier with the new threshold (AC4). No full page reload.
      void this.router.navigate(['/customer-pricing']);
    } catch (error) {
      console.error('Failed to save the outlier threshold', error);
      notify('Could not save the outlier threshold — please try again', 'error', 6000);
    } finally {
      this.saving = false;
    }
  }

  /** Open the clear-confirmation dialog (visible only when a threshold is configured). */
  openClearConfirm(): void {
    if (this.isConfigured) {
      this.clearConfirmVisible = true;
    }
  }

  /** Confirm clearing — PUT {threshold:null}, then return to the dashboard so flags are removed (AC4). */
  async confirmClear(): Promise<void> {
    this.saving = true;
    try {
      await firstValueFrom(
        this.crudService.updateDataWithoutId(this.endpoint, { threshold: null }),
      );
      this.currentThreshold = null;
      this.value = null;
      this.clearConfirmVisible = false;
      notify('Outlier threshold cleared', 'success', 2500);
      void this.router.navigate(['/customer-pricing']);
    } catch (error) {
      console.error('Failed to clear the outlier threshold', error);
      notify('Could not clear the outlier threshold — please try again', 'error', 6000);
    } finally {
      this.saving = false;
    }
  }

  /** Cancel — discard any edits and return to the dashboard. */
  cancel(): void {
    void this.router.navigate(['/customer-pricing']);
  }
}
